import { User } from "firebase/auth";
import { createLogger } from "../utils/logger";
import { analyticsService } from "./AnalyticsService";

const logger = createLogger("UserPropertiesService");

export interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  company?: string;
  jobRole?: string;
  userRole?: string;
  onboardingComplete?: boolean;
  googleServicesConnected?: boolean;
  accountCreated?: string;
  lastSignIn?: string;
  authProvider?: string;
}

class UserPropertiesService {
  /**
   * Set comprehensive user properties for analytics segmentation
   * Note: Basic user properties (uid, email, etc.) are set by AnalyticsService.setUser()
   * This method only sets additional segmentation properties
   */
  setUserProperties(user: User, additionalData?: Partial<UserProfile>): void {
    try {
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email || undefined,
        displayName: user.displayName || undefined,
        accountCreated: user.metadata.creationTime,
        lastSignIn: user.metadata.lastSignInTime,
        authProvider: user.providerData[0]?.providerId || "unknown",
        ...additionalData,
      };

      // Only set additional segmentation properties (basic properties handled by AnalyticsService)
      if (additionalData) {
        if (additionalData.company) {
          analyticsService.setUserProperty("company", additionalData.company);
        }
        if (additionalData.jobRole) {
          analyticsService.setUserProperty("job_role", additionalData.jobRole);
        }
        if (additionalData.userRole) {
          analyticsService.setUserProperty("user_role", additionalData.userRole);
        }
        if (additionalData.onboardingComplete !== undefined) {
          analyticsService.setUserProperty("onboarding_complete", additionalData.onboardingComplete);
        }
        if (additionalData.googleServicesConnected !== undefined) {
          analyticsService.setUserProperty("google_services_connected", additionalData.googleServicesConnected);
        }
      }

      logger.debug("Additional user properties set for analytics:", userProfile);
    } catch (error) {
      logger.error("Failed to set user properties:", error);
    }
  }

  /**
   * Update specific user property
   */
  updateUserProperty(key: string, value: string | number | boolean): void {
    try {
      analyticsService.setUserProperty(key, value);
      logger.debug(`User property updated: ${key} = ${value}`);
    } catch (error) {
      logger.error(`Failed to update user property ${key}:`, error);
    }
  }

  /**
   * Set onboarding completion properties
   */
  setOnboardingComplete(user: User, userInfo?: { company?: string; jobRole?: string }): void {
    this.setUserProperties(user, {
      onboardingComplete: true,
      company: userInfo?.company,
      jobRole: userInfo?.jobRole,
    });
  }

  /**
   * Set Google services connection status
   */
  setGoogleServicesStatus(connected: boolean, services?: string[]): void {
    this.updateUserProperty("google_services_connected", connected);
    if (services && services.length > 0) {
      this.updateUserProperty("connected_google_services", services.join(","));
    }
  }

  /**
   * Set user role for access control analytics
   */
  setUserRole(role: string): void {
    this.updateUserProperty("user_role", role);
  }

  /**
   * Track feature usage for user segmentation
   */
  trackFeatureUsage(feature: string, used = true): void {
    this.updateUserProperty(`feature_${feature}_used`, used);
  }

  /**
   * Set user engagement metrics
   */
  setEngagementMetrics(metrics: { totalSessions?: number; lastActiveDate?: string; featuresUsed?: string[]; averageSessionDuration?: number }): void {
    if (metrics.totalSessions !== undefined) {
      this.updateUserProperty("total_sessions", metrics.totalSessions);
    }
    if (metrics.lastActiveDate) {
      this.updateUserProperty("last_active_date", metrics.lastActiveDate);
    }
    if (metrics.featuresUsed) {
      this.updateUserProperty("features_used", metrics.featuresUsed.join(","));
    }
    if (metrics.averageSessionDuration !== undefined) {
      this.updateUserProperty("avg_session_duration", metrics.averageSessionDuration);
    }
  }

  /**
   * Set user preferences for personalization analytics
   */
  setUserPreferences(preferences: { theme?: string; language?: string; notifications?: boolean; defaultView?: string }): void {
    if (preferences.theme) {
      this.updateUserProperty("preferred_theme", preferences.theme);
    }
    if (preferences.language) {
      this.updateUserProperty("preferred_language", preferences.language);
    }
    if (preferences.notifications !== undefined) {
      this.updateUserProperty("notifications_enabled", preferences.notifications);
    }
    if (preferences.defaultView) {
      this.updateUserProperty("default_view", preferences.defaultView);
    }
  }

  /**
   * Clear user properties (useful for logout)
   */
  clearUserProperties(): void {
    // Note: Firebase Analytics doesn't have a direct way to clear user properties
    // They will be cleared when a new user is set
    logger.debug("User properties cleared (new user will be set)");
  }
}

// Export singleton instance
export const userPropertiesService = new UserPropertiesService();
export default userPropertiesService;
